import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { Download, Eye, EyeOff, FileUp, GitCompare, RefreshCw, Upload } from 'lucide-react';
import { Button } from '@/shared/ui/Button';
import { ConfirmDialog } from '@/shared/ui';
import { useImpersonation } from '@/shared/context/useImpersonation';
import { Select } from '@/shared/ui/Select';
import { Skeleton } from '@/shared/ui/Skeleton';
import { Spinner } from '@/shared/ui/Spinner';
import { useToast } from '@/shared/ui/Toast';
import {
  useAdConfigCreatePlan,
  useAdConfigDiff,
  useAdConfigDrafts,
  useAdConfigDraftOperations,
  useAdConfigExport,
  useAdConfigOverview,
  useAdConfigSync,
  useAdConfigResyncGoogle,
  useReplaceFromFile,
  useCreateAdConfigDrafts,
  useStageLiveUpdates,
  useDiscardAdConfigDraft,
  useRefreshAdConfigOverview,
} from '../../hooks/useAdConfig';
import type {
  AdConfigChange,
  AdConfigCampaignDiff,
  AdConfigConnection,
  AdConfigConnectionState,
  AdConfigDraftSummary,
  AdConfigExport,
  AdConfigOverview,
  CampaignDiff,
  CreateDraftsResult,
  CreateDraftView,
  CreatePlan,
  KeywordOperation,
  KeywordOperationType,
  PlanIssue,
  ReplaceCampaignCheck,
  ReplaceFromFileResult,
  GoogleResyncResult,
} from '../../api/adConfigApi';
import {
  checkFileScope,
  detectImportMode,
  parseAdConfigFile,
  type AdConfigFileCampaign,
  type AdConfigFileHeader,
} from './parseAdConfigFile';
import styles from './AdConfigTab.module.css';

/**
 * Read a location's live Google and Meta campaign settings out to a Markdown
 * file, and check an edited one back in.
 *
 * Scoped to a location rather than a practice because that is where ad accounts
 * actually live: Google authorises one customer for the whole practice, but Meta
 * assigns an ad account per location, so a practice-wide export would claim
 * coverage it does not have.
 *
 * Import is a two-step flow on purpose. Picking a file only stages it: the
 * file is parsed and scope-checked locally, which is instant. Comparing it is a
 * separate, explicit button because that step reads every campaign live from
 * Google and Meta and can take many seconds — so it should not fire silently on
 * file selection, and it shows a clear working state while it runs.
 *
 * Import is also deliberately incomplete past the diff: applying a confirmed
 * change needs an apply path that does not exist yet. The Apply control says so
 * rather than pretending — see the note at the bottom of the import panel.
 */

/** How often to re-check the overview while a queued sync is outstanding. */
const SYNC_POLL_MS = 4000;
/** Give up watching after this long; the job keeps running regardless. */
const SYNC_WATCH_TIMEOUT_MS = 90_000;

const STATE_LABEL: Record<AdConfigConnectionState, string> = {
  connected: 'Connected',
  stale: 'Stale',
  expired: 'Reconnect needed',
  missing: 'Not connected',
};

const CHANNEL_LABEL: Record<string, string> = {
  google_search: 'Search',
  google_pmax: 'Performance Max',
  google_display: 'Display',
  google_lsa: 'Local Services',
  meta: 'Meta',
};

const PLATFORM_LABEL: Record<string, string> = {
  google: 'Google Ads',
  meta: 'Meta Ads',
};

function formatAge(iso: string | null): string {
  if (!iso) return 'never';
  const hours = (Date.now() - new Date(iso).getTime()) / 3_600_000;
  if (hours < 1) return 'under an hour ago';
  if (hours < 48) return `${Math.round(hours)}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

// ─── Connections ──────────────────────────────────────────────────────────────

function ConnectionCard({
  connection,
  onSync,
  syncing,
  onResync,
  resyncing,
  onOpenSetup,
  onConnectGoogle,
}: {
  connection: AdConfigConnection;
  onSync: () => void;
  syncing: boolean;
  /** Google only: make PracticeNova's copy of the campaign settings match Google. */
  onResync?: () => void;
  resyncing?: boolean;
  onOpenSetup: () => void;
  onConnectGoogle: () => void;
}) {
  const reachable = connection.state === 'connected' || connection.state === 'stale';
  const isGoogle = connection.platform === 'google';
  // Google's connect/reconnect flow lives on the practice's own Settings → Integrations screen — the
  // Setup tab has nothing for Google. Meta stays on Setup: MetaAdAccountPicker lives there for real.
  const handleConnect = isGoogle ? onConnectGoogle : onOpenSetup;

  return (
    <article className={clsx(styles.conn, styles[`state${connection.state}`])}>
      <div className={styles.connTop}>
        <span className={styles.connName}>
          <span className={clsx(styles.platformMark, styles[connection.platform])} aria-hidden />
          {connection.label}
        </span>
        <span className={clsx(styles.pill, styles[`pill${connection.state}`])}>
          {STATE_LABEL[connection.state]}
        </span>
      </div>
      <div className={styles.connMeta}>
        {connection.account ?? 'no ad account'} · synced {formatAge(connection.lastSyncAt)} ·{' '}
        {connection.campaignCount} campaign{connection.campaignCount === 1 ? '' : 's'}
      </div>
      <p className={styles.connBody}>{connection.detail}</p>
      {connection.lastSyncError && (
        <p className={styles.connError}>Last sync failed: {connection.lastSyncError}</p>
      )}
      {syncing && <p className={styles.connBody}>Sync running…</p>}
      <div className={styles.connActions}>
        {reachable && (
          <Button
            size="sm"
            variant="secondary"
            leftIcon={<RefreshCw size={13} />}
            isLoading={syncing}
            onClick={onSync}
            title={
              isGoogle
                ? 'Syncs the Google Ads account for every location in this practice, not just this one'
                : undefined
            }
          >
            {isGoogle ? 'Sync now (whole practice)' : 'Sync now'}
          </Button>
        )}
        {reachable && isGoogle && onResync && (
          <Button
            size="sm"
            variant="secondary"
            leftIcon={<RefreshCw size={13} />}
            isLoading={resyncing}
            onClick={onResync}
            title="Reads every live campaign at this location from Google and makes PracticeNova's copy of its settings match, so edits made directly in Google Ads aren't undone by a later push. Writes nothing to Google."
          >
            Re-sync settings from Google
          </Button>
        )}
        {connection.state === 'expired' && (
          <Button size="sm" variant="primary" onClick={handleConnect}>
            {isGoogle ? 'Reconnect in Integrations' : 'Reconnect in Setup'}
          </Button>
        )}
        {connection.state === 'missing' && (
          <Button size="sm" variant="primary" onClick={handleConnect}>
            {isGoogle ? 'Connect in Integrations' : 'Connect in Setup'}
          </Button>
        )}
      </div>
    </article>
  );
}

// ─── Export ───────────────────────────────────────────────────────────────────

function ExportPanel({
  data,
  preview,
  previewOpen,
  exporting,
  onExport,
  onTogglePreview,
  onShowPreview,
  onSave,
}: {
  data: AdConfigOverview;
  preview: AdConfigExport | null;
  previewOpen: boolean;
  exporting: boolean;
  onExport: (thenDownload: boolean) => void;
  onTogglePreview: () => void;
  onShowPreview: () => void;
  onSave: (file: AdConfigExport) => void;
}) {
  const empty = data.campaigns.length === 0;

  // Active campaigns always sit above paused/ended ones, and removed-on-platform
  // rows sink to the bottom; within each band the platform's original order holds.
  const isPausedLike = (c: (typeof data.campaigns)[number]) =>
    !c.removedOnPlatform && (c.status === 'paused' || c.status === 'ended');
  const rank = (c: (typeof data.campaigns)[number]) =>
    c.removedOnPlatform ? 2 : isPausedLike(c) ? 1 : 0;
  const sortedCampaigns = [...data.campaigns].sort((a, b) => rank(a) - rank(b));

  return (
    <>
      {empty ? (
        <p className={styles.emptyNote}>
          No live campaigns for this location on any connected platform, so there is nothing to
          export yet.
        </p>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Platform</th>
                <th>Campaign</th>
                <th>Status</th>
                <th>Daily budget</th>
                <th>Settings last read</th>
              </tr>
            </thead>
            <tbody>
              {sortedCampaigns.map((campaign) => (
                <tr
                  key={`${campaign.platform}-${campaign.externalId}`}
                  className={clsx(
                    campaign.removedOnPlatform && styles.removedRow,
                    isPausedLike(campaign) && styles.pausedRow,
                  )}
                >
                  <td>
                    <span
                      className={clsx(styles.platformMarkInline, styles[campaign.platform])}
                      aria-hidden
                    />
                    {PLATFORM_LABEL[campaign.platform] ?? campaign.platform}
                  </td>
                  <td>
                    <div className={styles.cellName}>{campaign.name}</div>
                    <div className={styles.cellSub}>
                      {CHANNEL_LABEL[campaign.channel] ?? campaign.channel}
                    </div>
                  </td>
                  <td>
                    {campaign.removedOnPlatform ? (
                      <span className={styles.removedBadge}>removed · read-only</span>
                    ) : (
                      campaign.status
                    )}
                  </td>
                  <td className={styles.num}>
                    {campaign.dailyBudget === null ? '—' : `$${campaign.dailyBudget.toFixed(2)}`}
                  </td>
                  <td className={styles.cellSub}>
                    {campaign.settingsReadAt
                      ? `read ${formatAge(campaign.settingsReadAt)}`
                      : 'not read yet'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!empty && (
        <p className={styles.tableFootnote}>
          “Settings last read” updates when you export, preview, or re-read — each of those reads
          every setting live from the platform. Syncing refreshes the campaign inventory and metrics
          above, not this.
        </p>
      )}

      <div className={styles.actions}>
        <Button
          variant="primary"
          leftIcon={<Download size={14} />}
          disabled={empty}
          isLoading={exporting}
          onClick={() => onExport(true)}
        >
          Export and download
        </Button>
        {/*
          Two deliberately different actions. "Preview file" reuses the copy
          already read in this session when there is one, so looking at the file
          again costs nothing. "Re-read" always goes back to the platform, which
          is what you want after changing something in Ads Manager.
        */}
        <Button
          variant="secondary"
          leftIcon={<Eye size={14} />}
          disabled={empty}
          isLoading={exporting && !preview}
          onClick={() => (preview ? onShowPreview() : onExport(false))}
        >
          Preview file
        </Button>
        <Button
          variant="secondary"
          leftIcon={<RefreshCw size={14} />}
          disabled={empty}
          isLoading={exporting}
          onClick={() => onExport(false)}
        >
          Re-read all from platform
        </Button>
        {exporting && (
          <span className={styles.progress}>
            Reading {data.campaigns.length} campaign{data.campaigns.length === 1 ? '' : 's'} from
            the platform…
          </span>
        )}
      </div>

      {preview && (
        <div className={styles.filePreview}>
          <div className={styles.fileBar}>
            <span>{preview.filename}</span>
            <span>
              fingerprint {preview.fingerprint} · read {formatAge(preview.generatedAt)}
            </span>
          </div>
          {previewOpen && <pre className={styles.md}>{preview.markdown}</pre>}
          <div className={styles.fileFoot}>
            <Button
              size="sm"
              variant="secondary"
              leftIcon={previewOpen ? <EyeOff size={13} /> : <Eye size={13} />}
              onClick={onTogglePreview}
            >
              {previewOpen ? 'Hide preview' : 'Show preview'}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              leftIcon={<Download size={13} />}
              onClick={() => onSave(preview)}
            >
              Download this file
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Import ───────────────────────────────────────────────────────────────────

interface LoadedFile {
  name: string;
  rawText: string;
  /** 'edit' reconciles existing campaigns; 'create' stands up new ones. */
  mode: 'edit' | 'create';
  /** Present only in edit mode, from the export header pre-flight. */
  header?: AdConfigFileHeader;
  campaigns?: AdConfigFileCampaign[];
}

const CAMPAIGN_STATE_META: Record<AdConfigCampaignDiff['state'], { tag: string; cls: string }> = {
  changed: { tag: 'Changed', cls: 'tagMatched' },
  added: { tag: 'In file, not live', cls: 'tagGone' },
  removed: { tag: 'Live, not in file', cls: 'tagNew' },
};

function ChangeLine({ change }: { change: AdConfigChange }) {
  if (change.kind === 'changed') {
    return (
      <div className={styles.changeRow}>
        <span className={styles.changeLabel}>{change.label}</span>
        <span className={styles.diffOld}>{change.before}</span>
        <span className={styles.diffArrow}>→</span>
        <span className={styles.diffNew}>{change.after}</span>
      </div>
    );
  }
  if (change.kind === 'added') {
    return (
      <div className={styles.changeRow}>
        <span className={clsx(styles.diffMark, styles.diffAdded)}>added</span>
        <span className={styles.diffNew}>{change.after}</span>
      </div>
    );
  }
  return (
    <div className={styles.changeRow}>
      <span className={clsx(styles.diffMark, styles.diffRemoved)}>removed</span>
      <span className={styles.diffOld}>{change.before}</span>
    </div>
  );
}

const DRAFT_STATUS_META: Record<string, { label: string; cls: string }> = {
  pending_review: { label: 'Pending review', cls: 'tagGone' },
  pushed: { label: 'Pushed live', cls: 'tagMatched' },
  discarded: { label: 'Discarded', cls: 'tagNew' },
};

const OP_META: Record<KeywordOperationType, { label: string; cls: string }> = {
  add_keyword: { label: 'Add keyword', cls: 'opAdd' },
  remove_keyword: { label: 'Remove keyword', cls: 'opRemove' },
  pause_keyword: { label: 'Pause keyword', cls: 'opPause' },
  enable_keyword: { label: 'Enable keyword', cls: 'opPause' },
  add_negative: { label: 'Add negative', cls: 'opNeg' },
  remove_negative: { label: 'Remove negative', cls: 'opRemove' },
};

function OperationRow({ op }: { op: KeywordOperation }) {
  const meta = OP_META[op.type];
  const target =
    op.scope === 'campaign'
      ? op.campaign.name
      : `${op.campaign.name} › ${op.adGroup?.name ?? 'ad group'}`;
  return (
    <div className={styles.opRow}>
      <span className={clsx(styles.opTag, styles[meta.cls])}>{meta.label}</span>
      <div className={styles.opBody}>
        <div className={styles.opMain}>
          <span className={styles.opKw}>{op.keyword}</span>
          {op.matchType && <span className={styles.opMatch}>{op.matchType}</span>}
          {op.before && op.after && (
            <span className={styles.opTransition}>
              {op.before} → {op.after}
            </span>
          )}
        </div>
        <div className={styles.opTarget}>{target}</div>
      </div>
    </div>
  );
}

/**
 * The dry run for one draft: the exact keyword operations it would push, and
 * the literal platform mutations behind them. Reads live and writes nothing —
 * the actual push is a later phase, so there is no approve control here.
 */
function DraftOperations({
  practiceId,
  locationId,
  draftId,
}: {
  practiceId: string;
  locationId: string;
  draftId: string;
}) {
  const [showExact, setShowExact] = useState(false);
  const { data, isPending, isError, error, refetch } = useAdConfigDraftOperations(
    practiceId,
    locationId,
    draftId,
    true,
  );

  if (isPending) {
    return (
      <div className={styles.opsPanel}>
        <div className={styles.working} role="status" aria-live="polite">
          <Spinner size="sm" />
          <div>
            <strong className={styles.workingTitle}>Reading live and resolving operations…</strong>
            <p className={styles.workingSub}>
              Comparing this file to what is live now and working out the exact keyword changes.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles.opsPanel}>
        <div className={clsx(styles.callout, styles.calloutError)}>
          <div>
            <strong>Could not build the preview.</strong>
            <p className={styles.calloutBody}>
              {error instanceof Error ? error.message : 'The dry run failed.'}
            </p>
          </div>
          <Button size="sm" variant="secondary" onClick={() => refetch()}>
            Try again
          </Button>
        </div>
      </div>
    );
  }

  const ops = data?.operations ?? [];

  return (
    <div className={styles.opsPanel}>
      <div className={styles.opsDryRun}>
        <strong>Dry run.</strong> Nothing is pushed to Google. This is the exact set of keyword
        changes a push would make, resolved against what is live right now. Budget, creative and
        targeting changes from the file are not pushable yet and stay in the change set.
      </div>

      {ops.length === 0 ? (
        <p className={styles.progress}>
          No pushable keyword changes — this file&rsquo;s differences are all outside the keyword
          scope (budgets, creative, targeting), or already match live.
        </p>
      ) : (
        <>
          <div className={styles.opsList}>
            {ops.map((op) => (
              <OperationRow key={op.id} op={op} />
            ))}
          </div>
          <div className={styles.opsFoot}>
            <Button
              size="sm"
              variant="secondary"
              leftIcon={showExact ? <EyeOff size={13} /> : <Eye size={13} />}
              onClick={() => setShowExact((v) => !v)}
            >
              {showExact ? 'Hide exact changes' : 'Show exact changes'}
            </Button>
          </div>
          {showExact && (
            <pre className={styles.opsExact}>{ops.map((op) => op.preview).join('\n')}</pre>
          )}
        </>
      )}
    </div>
  );
}

function DraftsList({
  practiceId,
  locationId,
  drafts,
  onDiscard,
  discardingId,
}: {
  practiceId: string;
  locationId: string;
  drafts: AdConfigDraftSummary[];
  onDiscard: (id: string) => void;
  discardingId: string | null;
}) {
  const [openId, setOpenId] = useState<string | null>(null);
  if (drafts.length === 0) return null;

  return (
    <div className={styles.drafts}>
      <div className={styles.draftsHead}>
        <span className={styles.cellName}>Saved change sets</span>
        <span className={styles.draftsNote}>
          Applied files are recorded here for review. Preview the keyword changes a push would make
          below — pushing them live to Google is a separate step (coming next).
        </span>
      </div>
      {drafts.map((draft) => {
        const meta = DRAFT_STATUS_META[draft.status] ?? {
          label: draft.status,
          cls: 'tagNew',
        };
        const open = openId === draft.id;
        return (
          <div key={draft.id}>
            <div className={styles.draftRow}>
              <div className={styles.draftMain}>
                <span className={styles.draftName}>{draft.sourceFilename ?? 'Uploaded file'}</span>
                <span className={styles.draftMeta}>
                  {draft.changedCount} campaign{draft.changedCount === 1 ? '' : 's'} · saved{' '}
                  {formatAge(draft.createdAt)}
                </span>
              </div>
              <span className={clsx(styles.tag, styles[meta.cls])}>{meta.label}</span>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setOpenId(open ? null : draft.id)}
              >
                {open ? 'Hide preview' : 'Preview push (dry run)'}
              </Button>
              {draft.status === 'pending_review' && (
                <Button
                  size="sm"
                  variant="secondary"
                  isLoading={discardingId === draft.id}
                  onClick={() => onDiscard(draft.id)}
                >
                  Discard
                </Button>
              )}
            </div>
            {open && (
              <DraftOperations practiceId={practiceId} locationId={locationId} draftId={draft.id} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Import: create-from-file plan ────────────────────────────────────────────

function PlanIssueLine({ issue }: { issue: PlanIssue }) {
  return (
    <div
      className={clsx(styles.issue, issue.level === 'error' ? styles.issueError : styles.issueWarn)}
    >
      <span className={styles.issueBadge}>{issue.level === 'error' ? 'Error' : 'Warning'}</span>
      <span className={styles.issueText}>
        <strong>{issue.field}</strong> — {issue.message}
      </span>
    </div>
  );
}

function CreateDraftCard({ draft }: { draft: CreateDraftView }) {
  const ready = draft.errorCount === 0;
  const isMeta = draft.channel === 'meta';
  return (
    <section className={styles.planDraft}>
      <div className={styles.planDraftHead}>
        <div className={styles.planDraftMain}>
          <span className={styles.planDraftName}>
            <span className={styles.cellName}>{draft.data.campaignName}</span>
            <span className={clsx(styles.miniChip, isMeta ? styles.miniMeta : styles.miniGoogle)}>
              {isMeta ? 'Meta' : 'Google'}
            </span>
            <span
              className={clsx(
                styles.miniChip,
                draft.existingLive
                  ? styles.miniLive
                  : draft.existing
                    ? styles.miniExists
                    : styles.miniNew,
              )}
            >
              {draft.existingLive ? 'Live' : draft.existing ? 'Exists' : 'New'}
            </span>
          </span>
          <span className={styles.planDraftMeta}>
            {draft.channel === 'meta'
              ? `$${draft.data.dailyBudget.toFixed(2)}/day · ${draft.data.objective} · ${draft.data.ads.length} ad${draft.data.ads.length === 1 ? '' : 's'} · ages ${draft.data.targeting.ageMin}–${draft.data.targeting.ageMax} · ${draft.data.placements.length} placements`
              : `$${draft.data.dailyBudget.toFixed(2)}/day · ${draft.data.keywords.length} keywords · ${draft.data.headlines.length} headlines · ${draft.data.descriptions.length} descriptions · zips ${draft.data.zipCodes.join(', ') || '—'}`}
          </span>
        </div>
        <span className={clsx(styles.tag, ready ? styles.tagMatched : styles.tagBlocked)}>
          {ready ? 'Ready' : `${draft.errorCount} error${draft.errorCount === 1 ? '' : 's'}`}
        </span>
      </div>
      {draft.existingLive && draft.diff && <LiveDiffView diff={draft.diff} />}
      {draft.issues.length > 0 && (
        <div className={styles.planIssues}>
          {draft.issues.map((issue, i) => (
            <PlanIssueLine key={i} issue={issue} />
          ))}
        </div>
      )}
    </section>
  );
}

const MECHANISM_LABEL: Record<string, string> = {
  live_edit: 'live edit',
  replace: 'replaces ad',
  recreate: 'needs recreate',
};

function LiveDiffView({ diff }: { diff: CampaignDiff }) {
  if (!diff.hasChanges) {
    return (
      <div className={styles.liveDiff}>
        <span className={styles.liveDiffNone}>
          Already live and matches this file — no changes to push.
        </span>
      </div>
    );
  }
  return (
    <div className={styles.liveDiff}>
      <div className={styles.liveDiffHead}>
        This campaign is <strong>live</strong> on the platform. Re-import doesn&rsquo;t change it —
        review the changes below; pushing them is a separate step.
      </div>
      {diff.changes.map((c, i) => (
        <div key={i} className={styles.liveDiffRow}>
          <span className={styles.liveDiffField}>{c.label}</span>
          <span className={styles.liveDiffSummary}>{c.summary}</span>
          <span className={styles.liveDiffTags}>
            <span className={clsx(styles.diffTag, styles[`diffMech_${c.mechanism}`])}>
              {MECHANISM_LABEL[c.mechanism] ?? c.mechanism}
            </span>
            {c.money && (
              <span className={clsx(styles.diffTag, styles.diffMoney)}>affects spend</span>
            )}
            {c.status && <span className={clsx(styles.diffTag, styles.diffStatus)}>status</span>}
          </span>
        </div>
      ))}
    </div>
  );
}

function CreatePlanView({
  file,
  plan,
  pending,
  errored,
  errorMessage,
  onCheck,
  onReplace,
  onCreate,
  creating,
  created,
  onStageLive,
  stagingLive,
  stagedLive,
}: {
  file: LoadedFile;
  plan: CreatePlan | null;
  pending: boolean;
  errored: boolean;
  errorMessage: string | null;
  onCheck: () => void;
  onReplace: () => void;
  onCreate: (strategy: 'skip' | 'update') => void;
  creating: boolean;
  created: CreateDraftsResult | null;
  onStageLive: () => void;
  stagingLive: boolean;
  stagedLive: number | null;
}) {
  // Default to updating existing campaigns on re-import — the operator's
  // preference — but let them switch to skip when collisions are present.
  const [strategy, setStrategy] = useState<'skip' | 'update'>('update');
  return (
    <>
      <div className={clsx(styles.callout, styles.calloutOk)}>
        <div>
          <strong>{file.name}</strong>
          <p className={styles.calloutBody}>
            Authored file — new campaigns to create for this location. Nothing is created until you
            check it and then create the drafts.
          </p>
        </div>
        <Button size="sm" variant="secondary" disabled={pending} onClick={onReplace}>
          Replace file
        </Button>
      </div>

      {!pending && (
        <div className={styles.compareBar}>
          <p className={styles.compareText}>
            {plan
              ? 'Plan below reflects the file as checked. Re-check after editing it.'
              : 'This validates the file against Google’s and Meta’s rules and shows what would be created. Nothing is written.'}
          </p>
          <Button
            variant="primary"
            leftIcon={plan ? <RefreshCw size={14} /> : <GitCompare size={14} />}
            onClick={onCheck}
          >
            {plan ? 'Re-check file' : 'Check file'}
          </Button>
        </div>
      )}

      {pending && (
        <div className={styles.working} role="status" aria-live="polite">
          <Spinner size="md" />
          <div>
            <strong className={styles.workingTitle}>Checking the file…</strong>
            <p className={styles.workingSub}>
              Parsing the campaigns and validating them against Google’s and Meta’s limits.
            </p>
          </div>
        </div>
      )}

      {errored && (
        <div className={clsx(styles.callout, styles.calloutError)}>
          <div>
            <strong>Could not check the file.</strong>
            <p className={styles.calloutBody}>{errorMessage ?? 'The check failed.'}</p>
          </div>
          <Button size="sm" variant="secondary" onClick={onCheck}>
            Try again
          </Button>
        </div>
      )}

      {plan && !pending && (
        <>
          {plan.fileIssues.length > 0 && (
            <div className={styles.planIssues}>
              {plan.fileIssues.map((issue, i) => (
                <PlanIssueLine key={i} issue={issue} />
              ))}
            </div>
          )}

          {plan.drafts.length > 0 && (
            <p className={styles.diffSummary}>
              <strong>
                {plan.summary.sourceCampaigns} campaign
                {plan.summary.sourceCampaigns === 1 ? '' : 's'} → {plan.summary.drafts} draft
                {plan.summary.drafts === 1 ? '' : 's'}.
              </strong>{' '}
              {plan.summary.readyNew} new · {plan.summary.existing - plan.summary.existingLive}{' '}
              draft {plan.summary.existing - plan.summary.existingLive === 1 ? 'update' : 'updates'}
              {plan.summary.existingLive > 0 && (
                <> · {plan.summary.existingLive} live (review)</>
              )} · {plan.summary.errors} error{plan.summary.errors === 1 ? '' : 's'} ·{' '}
              {plan.summary.warnings} warning{plan.summary.warnings === 1 ? '' : 's'}.
            </p>
          )}

          {plan.drafts.length > 0 && (
            <div className={styles.launchNote}>
              <strong>What reaches the ad platform when you launch.</strong> Creating drafts writes
              only to PracticeNova. When a draft is later approved and launched, it is created{' '}
              <strong>paused</strong> — nothing spends until someone enables it on the platform.
              <br />
              <span className={styles.launchOk}>Google — sent:</span> budget, bid strategy,
              keywords, ad headlines &amp; descriptions, display paths, sitelinks, callouts,
              negatives (up to 50), zip-code targeting, languages, and the search-partner / display
              / location-targeting settings. <span className={styles.launchGap}>Not sent yet:</span>{' '}
              headline pinning, ad schedule (day-parting), and call-asset phone numbers.
              <br />
              <span className={styles.launchOk}>Meta — sent:</span> objective, ad-set budget, age /
              gender targeting, placements, and each ad’s primary text, headline, description and
              call to action. <span className={styles.launchGap}>Not sent yet:</span> the ad image
              (upload it on the campaign before launch), radius / address location targeting,
              detailed interests, and lifetime-budget or schedule settings. Advantage+ (automatic)
              placements are approximated as feed, stories and reels on both platforms.
            </div>
          )}

          {plan.drafts.map((draft, i) => (
            <CreateDraftCard key={i} draft={draft} />
          ))}

          {plan.skipped.length > 0 && (
            <div className={styles.callout}>
              <div>
                <strong>Not imported</strong>
                <p className={styles.calloutBody}>
                  {plan.skipped.map((s, i) => (
                    <span key={i}>
                      {s.name} — {s.reason}{' '}
                    </span>
                  ))}
                </p>
              </div>
            </div>
          )}

          {plan.drafts.length > 0 && plan.summary.existingLive > 0 && (
            <div className={styles.callout}>
              <div>
                <strong>{plan.summary.existingLive} already live on the platform</strong>
                <p className={styles.calloutBody}>
                  Re-import never edits a live campaign directly. Stage these changes as a{' '}
                  <strong>pending revision</strong> for review on each campaign&rsquo;s Setup tab;
                  pushing them to Google/Meta is a separate step. The create/update action below
                  only touches drafts.
                </p>
              </div>
              {stagedLive !== null ? (
                <Button variant="secondary" size="sm" disabled>
                  Staged {stagedLive} ✓
                </Button>
              ) : (
                <Button variant="secondary" size="sm" isLoading={stagingLive} onClick={onStageLive}>
                  Stage {plan.summary.existingLive} for review
                </Button>
              )}
            </div>
          )}

          {plan.drafts.length > 0 &&
            !created &&
            plan.summary.existing - plan.summary.existingLive > 0 && (
              <div className={styles.strategyBar}>
                <span className={styles.strategyLabel}>
                  {plan.summary.existing - plan.summary.existingLive} of these already exist as
                  drafts. On import:
                </span>
                <label className={styles.strategyOpt}>
                  <input
                    type="radio"
                    name="existingStrategy"
                    checked={strategy === 'update'}
                    onChange={() => setStrategy('update')}
                  />
                  Update them with this file
                </label>
                <label className={styles.strategyOpt}>
                  <input
                    type="radio"
                    name="existingStrategy"
                    checked={strategy === 'skip'}
                    onChange={() => setStrategy('skip')}
                  />
                  Leave them, create only new
                </label>
              </div>
            )}

          {plan.drafts.length > 0 &&
            (() => {
              // Live matches are review-only (block-and-choose); only draft
              // matches are updatable by this action.
              const draftUpdates = plan.summary.existing - plan.summary.existingLive;
              const willUpdate = strategy === 'update' ? draftUpdates : 0;
              const willCreate = plan.summary.readyNew;
              const actionable = willCreate + willUpdate;
              const label =
                [
                  willCreate > 0 ? `Create ${willCreate}` : null,
                  willUpdate > 0 ? `Update ${willUpdate}` : null,
                ]
                  .filter(Boolean)
                  .join(' · ') || 'Nothing to do';
              return (
                <div className={styles.applyBar}>
                  {created ? (
                    <>
                      <p className={styles.applyText}>
                        {created.created === 0 && created.updated === 0 ? (
                          <>
                            <strong>No changes.</strong> These campaigns already exist and were left
                            as they are.
                          </>
                        ) : (
                          <>
                            <strong>
                              {[
                                created.created > 0 ? `Created ${created.created}` : null,
                                created.updated > 0 ? `Updated ${created.updated}` : null,
                              ]
                                .filter(Boolean)
                                .join(', ')}
                              .
                            </strong>{' '}
                            {created.skippedLive > 0 &&
                              `${created.skippedLive} live campaign${created.skippedLive === 1 ? '' : 's'} left unchanged. `}
                            They&rsquo;re in the practice&rsquo;s <strong>Campaigns</strong> view
                            for review. Nothing was pushed to Google — approving and launching a
                            draft is the next step.
                          </>
                        )}
                      </p>
                      <Button variant="secondary" disabled>
                        {created.created === 0 && created.updated === 0 ? 'No changes' : 'Done ✓'}
                      </Button>
                    </>
                  ) : (
                    <>
                      <p className={styles.applyText}>
                        {actionable === 0 ? (
                          <>
                            <strong>Nothing to do.</strong> Every campaign still has an error to fix
                            in the file.
                          </>
                        ) : (
                          <>
                            <strong>Import to PracticeNova.</strong> {willCreate} new
                            {willUpdate > 0 && `, ${willUpdate} updated`} — as drafts in the
                            Campaigns view.
                            {plan.summary.errors > 0 &&
                              ` The ${plan.summary.errors} with errors are skipped until the file is fixed.`}{' '}
                            Nothing is pushed to Google.
                          </>
                        )}
                      </p>
                      <Button
                        variant="primary"
                        isLoading={creating}
                        disabled={actionable === 0}
                        onClick={() => onCreate(strategy)}
                      >
                        {label}
                      </Button>
                    </>
                  )}
                </div>
              );
            })()}
        </>
      )}
    </>
  );
}

/** What a replace did, per campaign, with a way into each replaced one. */
function ResyncResultList({ result }: { result: GoogleResyncResult }) {
  return (
    <div className={styles.replaceList}>
      <div className={styles.replaceRow}>
        <div className={styles.draftMain}>
          <span className={styles.draftName}>Re-synced from Google</span>
          <span className={styles.draftMeta}>
            {result.refreshed.length} updated · {result.unchanged.length} already matched ·{' '}
            {result.skipped.length} not read
            {result.metricsSyncQueued ? ' · metrics sync queued' : ''}
          </span>
          <span className={styles.draftMeta}>{result.agent.note}</span>
        </div>
      </div>
      {result.refreshed.map((r) => (
        <div key={r.deploymentId} className={styles.replaceRow}>
          <div className={styles.draftMain}>
            <span className={styles.draftName}>{r.name}</span>
            {r.changes.map((c) => (
              <span key={`${c.label}-${c.summary}`} className={styles.draftMeta}>
                {c.label}: {c.summary}
              </span>
            ))}
            {r.stagedUpdateRebased && (
              <span className={styles.draftMeta}>
                Its staged update now keeps only its own changes on top of Google’s settings.
              </span>
            )}
          </div>
        </div>
      ))}
      {result.skipped.map((s) => (
        <div key={s.name} className={styles.replaceRow}>
          <div className={styles.draftMain}>
            <span className={styles.draftName}>{s.name}</span>
            <span className={styles.draftMeta}>Not read: {s.reason}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * What Replace will do with one campaign, shown under its comparison before
 * Replace is clicked — from the same checks Replace runs, so a campaign the
 * comparison shows can't be refused afterwards without warning.
 */
function ReplaceCheckBox({ check }: { check: ReplaceCampaignCheck | undefined }) {
  if (!check) return null;
  if (check.outcome === 'blocked') {
    return (
      <div className={clsx(styles.checkBox, styles.checkBlocked)} role="alert">
        <strong>Will not be replaced — the whole campaign is skipped.</strong> {check.reason}
      </div>
    );
  }
  if (check.outcome === 'unchanged' && check.skipped.length === 0) {
    return (
      <div className={styles.checkBox}>
        <strong>Nothing to replace.</strong> PracticeNova already holds what this file asks for, so
        Replace leaves this campaign as it is.
      </div>
    );
  }
  return (
    <div className={clsx(styles.checkBox, check.skipped.length > 0 && styles.checkSkipped)}>
      {check.outcome === 'replace' ? (
        <strong>
          Replace will apply {check.changes.length} change{check.changes.length === 1 ? '' : 's'}
          {check.skipped.length > 0 ? ' and skip what’s listed below.' : '.'}
        </strong>
      ) : (
        <strong>Nothing will be replaced — every change in this file is skipped:</strong>
      )}
      {check.changes.length > 0 && (
        <ul className={styles.checkList}>
          {check.changes.map((c, i) => (
            <li key={`c${i}`}>{c.summary}</li>
          ))}
        </ul>
      )}
      {check.skipped.length > 0 && (
        <ul className={styles.checkList}>
          {check.skipped.map((s, i) => (
            <li key={`s${i}`}>Skipped: {s}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ReplaceResultList({
  result,
  onOpen,
}: {
  result: ReplaceFromFileResult;
  onOpen: (marketingCampaignId: string) => void;
}) {
  return (
    <div className={styles.replaceList}>
      {result.replaced.map((r) => (
        <div key={r.deploymentId} className={styles.replaceRow}>
          <div className={styles.draftMain}>
            <span className={styles.draftName}>{r.name}</span>
            <span className={styles.draftMeta}>
              {r.changes} change{r.changes === 1 ? '' : 's'} · not on Google yet
            </span>
            {r.notes.map((note) => (
              <span key={note} className={styles.draftMeta}>
                {note}
              </span>
            ))}
          </div>
          {r.marketingCampaignId && (
            <Button size="sm" variant="secondary" onClick={() => onOpen(r.marketingCampaignId!)}>
              Open campaign
            </Button>
          )}
        </div>
      ))}
      {result.unchanged.map((name) => (
        <div key={name} className={styles.replaceRow}>
          <div className={styles.draftMain}>
            <span className={styles.draftName}>{name}</span>
            <span className={styles.draftMeta}>Already matches this file — nothing to replace</span>
          </div>
        </div>
      ))}
      {result.skipped.map((s) => (
        <div key={`${s.name}-${s.reason}`} className={styles.replaceRow}>
          <div className={styles.draftMain}>
            <span className={styles.draftName}>{s.name}</span>
            <span className={styles.draftMeta}>Not replaced — {s.reason}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function ImportPanel({
  practiceId,
  practiceName,
  locationId,
}: {
  practiceId: string;
  practiceName: string;
  locationId: string;
}) {
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<LoadedFile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const diffMutation = useAdConfigDiff(practiceId, locationId);
  const replaceMutation = useReplaceFromFile(practiceId, locationId);
  const [confirmReplace, setConfirmReplace] = useState(false);
  const navigate = useNavigate();
  const { startImpersonation } = useImpersonation();
  const createPlanMutation = useAdConfigCreatePlan(practiceId, locationId);
  const createDraftsMutation = useCreateAdConfigDrafts(practiceId, locationId);
  const stageLiveMutation = useStageLiveUpdates(practiceId, locationId);
  const draftsQuery = useAdConfigDrafts(practiceId, locationId);
  const discardMutation = useDiscardAdConfigDraft(practiceId, locationId);
  const diff = diffMutation.data?.diff ?? null;
  const createPlan = createPlanMutation.data ?? null;

  const runDiff = useCallback(
    (loaded: LoadedFile) => {
      // A fresh comparison supersedes any earlier replace result, so the
      // button offers to replace from this comparison rather than a stale one.
      replaceMutation.reset();
      diffMutation.mutate(loaded.rawText);
    },
    [diffMutation, replaceMutation],
  );

  const runCreatePlan = useCallback(
    (loaded: LoadedFile) => {
      createDraftsMutation.reset();
      createPlanMutation.mutate(loaded.rawText, {
        onError: (err: unknown) =>
          toast.error(err instanceof Error ? err.message : 'Could not check the file.'),
      });
    },
    [createPlanMutation, createDraftsMutation, toast],
  );

  const handleCreateDrafts = useCallback(
    (existingStrategy: 'skip' | 'update') => {
      if (!file) return;
      createDraftsMutation.mutate(
        { markdown: file.rawText, existingStrategy },
        {
          onSuccess: (res) => {
            const parts = [
              res.created > 0 ? `${res.created} created` : null,
              res.updated > 0 ? `${res.updated} updated` : null,
              res.skippedExisting > 0 ? `${res.skippedExisting} skipped` : null,
              res.skippedLive > 0 ? `${res.skippedLive} live (unchanged)` : null,
            ].filter(Boolean);
            if (res.created === 0 && res.updated === 0) {
              toast.info(`No changes — ${parts.join(' · ') || 'nothing to do'}.`);
            } else {
              toast.success(`Done — ${parts.join(' · ')}.`);
            }
          },
          onError: (err: unknown) =>
            toast.error(err instanceof Error ? err.message : 'Could not create the drafts.'),
        },
      );
    },
    [file, createDraftsMutation, toast],
  );

  const handleStageLive = useCallback(() => {
    if (!file) return;
    stageLiveMutation.mutate(
      { markdown: file.rawText },
      {
        onSuccess: (res) =>
          toast.success(
            res.staged > 0
              ? `Staged ${res.staged} live change${res.staged === 1 ? '' : 's'} — review on each campaign's Setup tab.`
              : 'Nothing to stage.',
          ),
        onError: (err: unknown) =>
          toast.error(err instanceof Error ? err.message : 'Could not stage the changes.'),
      },
    );
  }, [file, stageLiveMutation, toast]);

  // Replace exactly the Google campaigns the comparison marked as changed and
  // whose checks say Replace will take something from the file.
  const replaceChecks = diffMutation.data?.replaceChecks;
  const checkByKey = useMemo(
    () => new Map((replaceChecks ?? []).map((c) => [c.key, c])),
    [replaceChecks],
  );
  const replaceable = useMemo(
    () =>
      (diff?.campaigns ?? []).filter(
        (c) =>
          c.state === 'changed' &&
          c.key.startsWith('google:') &&
          checkByKey.get(c.key)?.outcome === 'replace',
      ),
    [diff, checkByKey],
  );
  const changedGoogleIds = useMemo(
    () => replaceable.map((c) => c.key.slice('google:'.length)),
    [replaceable],
  );
  const changedGoogleNames = useMemo(() => replaceable.map((c) => c.heading), [replaceable]);

  const handleReplace = useCallback(() => {
    if (!file) return;
    replaceMutation.mutate(
      { markdown: file.rawText, filename: file.name, externalIds: changedGoogleIds },
      {
        onSuccess: (res) =>
          toast.success(
            res.replaced.length > 0
              ? `Replaced ${res.replaced.length} campaign${res.replaced.length === 1 ? '' : 's'} in PracticeNova.`
              : 'Nothing was replaced — see the notes below.',
          ),
        onError: (err: unknown) =>
          toast.error(err instanceof Error ? err.message : 'Could not replace the campaigns.'),
      },
    );
  }, [file, replaceMutation, toast, changedGoogleIds]);

  // Opening a campaign lands inside the practice's own app, so it is an
  // impersonation — started explicitly (and audited), like other deep links.
  const openCampaign = useCallback(
    (marketingCampaignId: string) => {
      startImpersonation({ id: practiceId, name: practiceName });
      navigate(`/campaigns/${marketingCampaignId}`);
    },
    [startImpersonation, practiceId, practiceName, navigate],
  );

  const handleFile = useCallback(
    async (picked: File) => {
      setError(null);
      setFile(null);
      diffMutation.reset();
      replaceMutation.reset();
      createPlanMutation.reset();
      createDraftsMutation.reset();

      const text = await picked.text();
      const detected = detectImportMode(text);
      if (detected.mode === 'invalid') {
        setError(detected.reason);
        return;
      }

      if (detected.mode === 'create') {
        // Authored new-campaign file: the target location is this workspace, so
        // it needs no export header or scope check. Staged for an explicit check.
        setFile({ name: picked.name, rawText: text, mode: 'create' });
        return;
      }

      // Edit mode: an exported file that reconciles against live campaigns.
      const parsed = parseAdConfigFile(text);
      if (!parsed.ok) {
        setError(parsed.reason);
        return;
      }
      const scope = checkFileScope(parsed.header, practiceId, locationId);
      if (!scope.ok) {
        setError(scope.reason);
        return;
      }
      // Staging only. The live comparison is a separate, explicit step because
      // it reads every campaign back from the platform and is slow — running it
      // automatically here is what made selection feel like nothing happened.
      setFile({
        name: picked.name,
        rawText: text,
        mode: 'edit',
        header: parsed.header,
        campaigns: parsed.campaigns,
      });
    },
    [
      practiceId,
      locationId,
      diffMutation,
      replaceMutation,
      createPlanMutation,
      createDraftsMutation,
    ],
  );

  const handleDiscard = useCallback(
    (draftId: string) => {
      discardMutation.mutate(draftId, {
        onError: (err: unknown) =>
          toast.error(err instanceof Error ? err.message : 'Could not discard the draft.'),
      });
    },
    [discardMutation, toast],
  );

  const changed = diff?.campaigns.filter((c) => c.state === 'changed') ?? [];
  const inFileNotLive = diff?.campaigns.filter((c) => c.state === 'added') ?? [];
  const liveNotInFile = diff?.campaigns.filter((c) => c.state === 'removed') ?? [];
  const drafts = draftsQuery.data ?? [];

  return (
    <>
      <input
        ref={inputRef}
        id="ad-config-file"
        type="file"
        accept=".md,text/markdown,text/plain"
        className={styles.fileInput}
        onChange={(e) => {
          const picked = e.target.files?.[0];
          if (picked) void handleFile(picked);
          // Reset so picking the same file twice still fires a change.
          e.target.value = '';
        }}
      />

      <DraftsList
        practiceId={practiceId}
        locationId={locationId}
        drafts={drafts}
        onDiscard={handleDiscard}
        discardingId={discardMutation.isPending ? (discardMutation.variables ?? null) : null}
      />

      {!file && (
        <div className={styles.drop}>
          <FileUp size={22} aria-hidden />
          <p className={styles.dropTitle}>Upload an ad config file</p>
          <p className={styles.dropSub}>
            Edit an <strong>exported</strong> file to change existing campaigns, or upload an
            authored file of <strong>new</strong> campaigns to create them here. Both must start
            with the <code>&lt;!-- pn:ads-config v2 --&gt;</code> header.
          </p>
          <Button
            variant="primary"
            leftIcon={<Upload size={14} />}
            onClick={() => inputRef.current?.click()}
          >
            Choose a .md file
          </Button>
        </div>
      )}

      {error && (
        <div className={clsx(styles.callout, styles.calloutError)}>
          <div>
            <strong>That file cannot be used.</strong>
            <p className={styles.calloutBody}>{error}</p>
          </div>
          <Button size="sm" variant="secondary" onClick={() => inputRef.current?.click()}>
            Pick another file
          </Button>
        </div>
      )}

      {file && file.mode === 'create' && (
        <CreatePlanView
          file={file}
          plan={createPlan}
          pending={createPlanMutation.isPending}
          errored={createPlanMutation.isError}
          errorMessage={
            createPlanMutation.error instanceof Error ? createPlanMutation.error.message : null
          }
          onCheck={() => runCreatePlan(file)}
          onReplace={() => inputRef.current?.click()}
          onCreate={handleCreateDrafts}
          creating={createDraftsMutation.isPending}
          created={createDraftsMutation.data ?? null}
          onStageLive={handleStageLive}
          stagingLive={stageLiveMutation.isPending}
          stagedLive={stageLiveMutation.data?.staged ?? null}
        />
      )}

      {file && file.mode === 'edit' && file.header && file.campaigns && (
        <>
          <div className={clsx(styles.callout, styles.calloutOk)}>
            <div>
              <strong>{file.name}</strong>
              <p className={styles.calloutBody}>
                Format v{file.header.formatVersion} · exported {formatAge(file.header.exportedAt)} ·
                describes {file.campaigns.length} campaign
                {file.campaigns.length === 1 ? '' : 's'}. Checked out for this location.
              </p>
            </div>
            <Button
              size="sm"
              variant="secondary"
              disabled={diffMutation.isPending}
              onClick={() => inputRef.current?.click()}
            >
              Replace file
            </Button>
          </div>

          {/*
            The explicit trigger. Selection stages; this reads live and diffs.
            Once a diff exists the same button re-runs it, so a file that sat
            open while the platform changed can be re-checked without reloading.
          */}
          {!diffMutation.isPending && (
            <div className={styles.compareBar}>
              <p className={styles.compareText}>
                {diff
                  ? 'Comparison below reflects the platform as of when it ran. Re-compare to pull the latest.'
                  : 'Nothing is read from Google or Meta until you compare. This reads every campaign live, so it can take a few seconds.'}
              </p>
              <Button
                variant="primary"
                leftIcon={diff ? <RefreshCw size={14} /> : <GitCompare size={14} />}
                onClick={() => runDiff(file)}
              >
                {diff ? 'Re-compare against live' : 'Compare against live'}
              </Button>
            </div>
          )}

          {diffMutation.isPending && (
            <div className={styles.working} role="status" aria-live="polite">
              <Spinner size="md" />
              <div>
                <strong className={styles.workingTitle}>
                  Reading {file.campaigns.length} campaign
                  {file.campaigns.length === 1 ? '' : 's'} live from Google and Meta…
                </strong>
                <p className={styles.workingSub}>
                  Every setting is pulled fresh from the platform and compared to your file. This
                  can take several seconds — hold tight.
                </p>
              </div>
            </div>
          )}

          {diffMutation.isError && (
            <div className={clsx(styles.callout, styles.calloutError)}>
              <div>
                <strong>Could not compare the file.</strong>
                <p className={styles.calloutBody}>
                  {diffMutation.error instanceof Error
                    ? diffMutation.error.message
                    : 'The diff failed.'}
                </p>
              </div>
              <Button size="sm" variant="secondary" onClick={() => runDiff(file)}>
                Try again
              </Button>
            </div>
          )}

          {diff && (
            <>
              <p className={styles.diffSummary}>
                <strong>
                  {changed.length === 0
                    ? 'No differences.'
                    : `${changed.length} campaign${changed.length === 1 ? '' : 's'} changed.`}
                </strong>{' '}
                {diff.unchangedCount} unchanged
                {inFileNotLive.length > 0 && ` · ${inFileNotLive.length} in file but not live`}
                {liveNotInFile.length > 0 && ` · ${liveNotInFile.length} live but not in file`}.
              </p>

              {changed.map((campaign) => (
                <section key={campaign.key} className={styles.diffCampaign}>
                  <div className={styles.diffCampaignHead}>
                    <span className={styles.cellName}>{campaign.heading}</span>
                    <span className={clsx(styles.tag, styles.tagMatched)}>
                      {CAMPAIGN_STATE_META.changed.tag}
                    </span>
                  </div>
                  {campaign.sections.map((section) => (
                    <div
                      key={section.section}
                      className={clsx(
                        styles.diffSection,
                        section.ignored && styles.diffSectionIgnored,
                      )}
                    >
                      <div className={styles.diffSectionName}>{section.section}</div>
                      {section.note && <p className={styles.diffSectionNote}>{section.note}</p>}
                      {section.changes.map((change, i) => (
                        <ChangeLine key={i} change={change} />
                      ))}
                    </div>
                  ))}
                  <ReplaceCheckBox check={checkByKey.get(campaign.key)} />
                </section>
              ))}

              {(inFileNotLive.length > 0 || liveNotInFile.length > 0) && (
                <div className={clsx(styles.callout)}>
                  <div>
                    <strong>Campaigns that will not be touched</strong>
                    <p className={styles.calloutBody}>
                      {inFileNotLive.map((c) => (
                        <span key={c.key}>{c.heading} — in the file but not live. </span>
                      ))}
                      {liveNotInFile.map((c) => (
                        <span key={c.key}>{c.heading} — live but absent from the file. </span>
                      ))}
                    </p>
                  </div>
                </div>
              )}

              <div className={styles.applyBar}>
                {replaceMutation.data ? (
                  <p className={styles.applyText}>
                    <strong>
                      {replaceMutation.data.replaced.length > 0
                        ? 'Replaced in PracticeNova.'
                        : 'Nothing was replaced.'}
                    </strong>{' '}
                    {replaceMutation.data.replaced.length > 0
                      ? 'The campaign page now shows this file’s version, marked “not on Google yet”. Nothing was sent to Google — open the campaign and use Update on Google when you’re ready.'
                      : 'PracticeNova already matches this file, or the campaigns couldn’t be matched — see below.'}
                  </p>
                ) : (
                  <>
                    <p className={styles.applyText}>
                      {changed.length > 0 && changedGoogleIds.length === 0 ? (
                        <>
                          <strong>Nothing can be replaced from this file.</strong> See the notes
                          under each campaign above for why.
                        </>
                      ) : (
                        <>
                          <strong>Replace PracticeNova’s version with this file?</strong> The
                          campaign page will show the file&rsquo;s settings, apart from anything
                          listed as skipped above. Nothing changes on Google until you update it
                          from the campaign page.
                        </>
                      )}
                    </p>
                    <Button
                      variant="primary"
                      isLoading={replaceMutation.isPending}
                      disabled={changedGoogleIds.length === 0}
                      onClick={() => setConfirmReplace(true)}
                    >
                      Replace in PracticeNova
                    </Button>
                  </>
                )}
              </div>

              {replaceMutation.data && (
                <ReplaceResultList result={replaceMutation.data} onOpen={openCampaign} />
              )}

              <ConfirmDialog
                isOpen={confirmReplace}
                onClose={() => setConfirmReplace(false)}
                onConfirm={() => {
                  setConfirmReplace(false);
                  handleReplace();
                }}
                title="Replace in PracticeNova"
                message={`PracticeNova's copy of ${changedGoogleNames.join(', ')} will be replaced with this file. Google isn't changed until you use Update on Google on the campaign page.`}
                confirmLabel="Replace"
              />
            </>
          )}
        </>
      )}
    </>
  );
}

// ─── Tab ──────────────────────────────────────────────────────────────────────

type Mode = 'export' | 'import';

export function AdConfigTab({
  practiceId,
  practiceName,
  locations,
  onOpenSetup,
}: {
  practiceId: string;
  practiceName: string;
  locations: { id: string; name: string }[];
  onOpenSetup: () => void;
}) {
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const navigate = useNavigate();
  const { startImpersonation } = useImpersonation();

  const [locationId, setLocationId] = useState(locations[0]?.id ?? '');
  const [mode, setMode] = useState<Mode>('export');
  const [preview, setPreview] = useState<AdConfigExport | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Google's connect/reconnect has no home on the Setup tab (only Meta's account picker lives
  // there) — this is the same impersonate-then-navigate pattern ImportPanel uses to open a
  // campaign in the practice's own view.
  const openGoogleIntegrations = useCallback(() => {
    startImpersonation({ id: practiceId, name: practiceName });
    navigate('/settings/paid');
  }, [startImpersonation, practiceId, practiceName, navigate]);

  // What we are waiting on, if anything. `since` is the lastSyncAt value at the
  // moment the job was queued, which is what tells a completed sync apart from
  // a sync that has not started.
  const [awaitingSync, setAwaitingSync] = useState<{
    platform: 'google' | 'meta';
    since: string | null;
    startedAt: number;
  } | null>(null);

  const { data, isLoading, isError } = useAdConfigOverview(
    practiceId,
    locationId,
    awaitingSync ? SYNC_POLL_MS : false,
  );
  const exportMutation = useAdConfigExport(practiceId, locationId);
  const syncMutation = useAdConfigSync(practiceId, locationId);
  const resyncMutation = useAdConfigResyncGoogle(practiceId, locationId);
  const resetResync = resyncMutation.reset;
  const refreshOverview = useRefreshAdConfigOverview(practiceId, locationId);

  const locationOptions = useMemo(
    () => locations.map((l) => ({ value: l.id, label: l.name })),
    [locations],
  );

  const changeLocation = useCallback(
    (next: string) => {
      setLocationId(next);
      // The previous location's file has nothing to do with this one — keeping it
      // on screen under a new heading is how someone downloads the wrong file.
      setPreview(null);
      setPreviewOpen(false);
      // Likewise the sync being watched: it belongs to the location we just left,
      // and its result would be reported against the new one.
      setAwaitingSync(null);
      // And the last re-sync's report.
      resetResync();
    },
    [resetResync],
  );

  const saveFile = useCallback((file: AdConfigExport) => {
    const blob = new Blob([file.markdown], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = file.filename;
    anchor.click();
    URL.revokeObjectURL(url);
  }, []);

  const runExport = useCallback(
    (thenDownload: boolean) => {
      exportMutation.mutate(undefined, {
        onSuccess: (file) => {
          setPreview(file);
          setPreviewOpen(true);
          if (thenDownload) saveFile(file);
          // The export stamps settingsReadAt on every campaign it read, so the
          // inventory above is now out of date with the file just produced.
          refreshOverview();
          const failed = file.campaigns.filter((c) => c.coverage === 'none');
          if (failed.length > 0) {
            toast.warning(
              `${failed.length} campaign${failed.length === 1 ? '' : 's'} could not be read — the file lists them with the error.`,
            );
          }
        },
        onError: (err: unknown) => {
          toast.error(err instanceof Error ? err.message : 'Export failed');
        },
      });
    },
    [exportMutation, saveFile, toast, refreshOverview],
  );

  const runSync = useCallback(
    (platform: 'google' | 'meta') => {
      const before = data?.connections.find((c) => c.platform === platform)?.lastSyncAt ?? null;
      syncMutation.mutate(platform, {
        onSuccess: () => setAwaitingSync({ platform, since: before, startedAt: Date.now() }),
        onError: (err: unknown) =>
          toast.error(err instanceof Error ? err.message : 'Could not queue the sync'),
      });
    },
    [syncMutation, toast, data],
  );

  /**
   * Watch a queued sync through to an outcome.
   *
   * The sync endpoint returns as soon as the job is queued, so "queued" is all
   * the mutation can tell us. A worker does the work seconds to minutes later,
   * and on failure it flips the integration to `error` without moving
   * `lastSyncAt`. Polling the overview is the only way to report what actually
   * happened rather than leaving "queued" as the last word — which is how a
   * failed Meta sync used to look identical to one still running.
   *
   * `toast` is read through a ref because the toast context value is a fresh
   * object on every provider render, so depending on it here would re-run this
   * effect (and restart the timer below) every time any toast appears.
   */
  useEffect(() => {
    if (!awaitingSync || !data) return;

    const connection = data.connections.find((c) => c.platform === awaitingSync.platform);
    if (!connection) {
      setAwaitingSync(null);
      return;
    }

    const label = PLATFORM_LABEL[awaitingSync.platform];

    // Failure first: the worker marks the integration errored and leaves
    // lastSyncAt untouched, so the "did it move" test below would never fire.
    if (connection.state === 'expired') {
      setAwaitingSync(null);
      toastRef.current.error(
        connection.lastSyncError
          ? `${label} sync failed: ${connection.lastSyncError}`
          : awaitingSync.platform === 'google'
            ? `${label} sync failed — reconnect it from Settings → Integrations.`
            : `${label} sync failed — the connection needs reconnecting in Setup.`,
      );
      return;
    }

    if (connection.lastSyncAt !== awaitingSync.since) {
      setAwaitingSync(null);
      toastRef.current.success(`${label} sync finished.`);
    }
  }, [awaitingSync, data]);

  /**
   * Stop watching after the timeout.
   *
   * This needs its own timer rather than a clock check in the effect above:
   * React Query reuses the same `data` reference when a refetch returns
   * identical content, so a sync that never lands produces no dependency change
   * and that effect simply stops running. The job itself keeps going.
   */
  useEffect(() => {
    if (!awaitingSync) return;
    const label = PLATFORM_LABEL[awaitingSync.platform];
    const timer = setTimeout(() => {
      setAwaitingSync(null);
      toastRef.current.warning(
        `${label} sync is still running after ${Math.round(SYNC_WATCH_TIMEOUT_MS / 1000)}s. It will finish in the background — reopen this tab to check.`,
      );
    }, SYNC_WATCH_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [awaitingSync]);

  if (locations.length === 0) {
    return <div className={styles.empty}>This practice has no locations yet.</div>;
  }

  return (
    <div className={styles.tab}>
      <div className={styles.locBar}>
        <Select
          label="Location"
          id="ad-config-location"
          options={locationOptions}
          value={locationId}
          onChange={(e) => changeLocation(e.target.value)}
        />
        <p className={styles.locNote}>
          Ad accounts attach per location, so one file covers one location across both platforms.
        </p>
      </div>

      {isLoading && <Skeleton height={320} borderRadius="var(--card-radius)" />}
      {isError && <div className={styles.empty}>Could not load ad configuration.</div>}

      {data && (
        <>
          <section className={styles.panel}>
            <div className={styles.panelHeader}>
              <h2 className={styles.panelTitle}>Ad account connections</h2>
              <p className={styles.panelSubtitle}>
                Settings can only be read from a platform this location is connected to. A stale
                connection still exports correctly — the export reads live, and syncing only
                refreshes the campaign inventory and cached metrics shown here.
              </p>
            </div>
            <div className={styles.connGrid}>
              {data.connections.map((connection) => (
                <ConnectionCard
                  key={connection.platform}
                  connection={connection}
                  syncing={
                    (syncMutation.isPending && syncMutation.variables === connection.platform) ||
                    awaitingSync?.platform === connection.platform
                  }
                  onSync={() => runSync(connection.platform)}
                  onResync={() =>
                    resyncMutation.mutate(undefined, {
                      onError: (err) =>
                        toast.error(
                          err instanceof Error ? err.message : 'Could not re-sync from Google.',
                        ),
                    })
                  }
                  resyncing={resyncMutation.isPending}
                  onOpenSetup={onOpenSetup}
                  onConnectGoogle={openGoogleIntegrations}
                />
              ))}
            </div>
            {resyncMutation.data && <ResyncResultList result={resyncMutation.data} />}
          </section>

          <section className={styles.panel}>
            <div className={styles.panelHeader}>
              <div className={styles.panelHeaderRow}>
                <div>
                  <h2 className={styles.panelTitle}>Campaign settings file</h2>
                  <p className={styles.panelSubtitle}>
                    One Markdown file per location, covering Google and Meta together. Every setting
                    is re-read from the platform at export time rather than taken from the copy
                    cached when the campaign was first imported.
                  </p>
                </div>
                <div className={styles.seg} role="tablist" aria-label="Export or import">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={mode === 'export'}
                    className={clsx(styles.segButton, mode === 'export' && styles.segOn)}
                    onClick={() => setMode('export')}
                  >
                    Export
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={mode === 'import'}
                    className={clsx(styles.segButton, mode === 'import' && styles.segOn)}
                    onClick={() => setMode('import')}
                  >
                    Import
                  </button>
                </div>
              </div>
            </div>

            {mode === 'export' ? (
              <ExportPanel
                data={data}
                preview={preview}
                previewOpen={previewOpen}
                exporting={exportMutation.isPending}
                onExport={runExport}
                onTogglePreview={() => setPreviewOpen((open) => !open)}
                onShowPreview={() => setPreviewOpen(true)}
                onSave={saveFile}
              />
            ) : (
              <ImportPanel
                practiceId={practiceId}
                practiceName={practiceName}
                locationId={locationId}
              />
            )}
          </section>
        </>
      )}
    </div>
  );
}
